import React, { useState, useEffect, useRef } from 'react';
import { 
    Card, CardHeader, CardBody, Button, Spinner, Alert, Input, InputGroup
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBoxes, faEdit, faFilePdf, faSave, faTimes, faTrash, faUndo,
    faMagicWandSparkles, faUser, faPlus, faMicrophone, faMicrophoneSlash
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getNodeApiUrl } from '../../config/api.config';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const LOGO_URL = 'https://storage.googleapis.com/installamelo/65a14921ed1be038f6185b53/LOGO_CON%20SCRITTA%20nera_v02.png';

const JobMaterialsPanel = ({ jobId, jobCode, customerLabel, onRefresh }) => {
    const [materials, setMaterials] = useState([]);
    const [materialsRawText, setMaterialsRawText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Stati per il pannello di aggiunta
    const [showAddPanel, setShowAddPanel] = useState(false);
    const [addRawText, setAddRawText] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    
    // Stati per dettatura vocale
    const [isRecording, setIsRecording] = useState(false);
    const recognitionRef = useRef(null);
    
    // Stati per modifica inline nella vista principale
    const [editingIndex, setEditingIndex] = useState(null);
    const [inlineEdit, setInlineEdit] = useState({ quantity: 0, unit: '', description: '' });
    
    // Stati per eliminazione multipla
    const [markedForDeletion, setMarkedForDeletion] = useState(new Set());
    const [isSavingDeletions, setIsSavingDeletions] = useState(false);

    useEffect(() => {
        if (jobId) {
            loadMaterials();
        }
    }, [jobId]);

    const loadMaterials = async () => {
        setIsLoading(true);
        setError('');
        
        try {
            const token = localStorage.getItem('installamelo-auth-token');
            const response = await axios.get(
                getNodeApiUrl(`/jobs/materials/${jobId}`),
                { headers: { 'auth-token': token } }
            );
            
            if (response.data?.success) {
                setMaterials(response.data.materials || []);
                setMaterialsRawText(response.data.materialsRawText || '');
            }
        } catch (err) {
            console.error('[JobMaterialsPanel] Errore caricamento materiali:', err);
            setError('Errore nel caricamento dei materiali');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAddPanel = () => {
        setShowAddPanel(!showAddPanel);
        if (!showAddPanel) {
            setAddRawText('');
        }
    };

    const handleParseAndAdd = async () => {
        if (!addRawText.trim()) {
            return;
        }

        setIsParsing(true);
        try {
            const token = localStorage.getItem('installamelo-auth-token');
            const authUser = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
            const accountId = authUser.accountId;
            const userId = authUser.userId;
            const userLabel = localStorage.getItem('userLabel') || 'Utente';

            // Prima parsa il testo con AI
            const parseResponse = await axios.post(
                getNodeApiUrl('/openAI/parseMaterials'),
                { accountId, rawText: addRawText },
                { headers: { 'auth-token': token } }
            );

            if (parseResponse.data?.materials && parseResponse.data.materials.length > 0) {
                // Aggiungi i nuovi materiali alla lista esistente e salva
                const updatedMaterials = [...materials, ...parseResponse.data.materials];
                
                const saveResponse = await axios.patch(
                    getNodeApiUrl('/jobs/materials/update'),
                    {
                        jobId,
                        materials: updatedMaterials,
                        materialsRawText,
                        userId,
                        userLabel
                    },
                    { headers: { 'auth-token': token } }
                );

                if (saveResponse.data?.success) {
                    setMaterials(saveResponse.data.materials || []);
                    setAddRawText('');
                    setShowAddPanel(false);
                }
            }
        } catch (err) {
            console.error('Errore parsing/aggiunta materiali:', err);
        } finally {
            setIsParsing(false);
        }
    };

    const toggleRecording = () => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            alert('Il tuo browser non supporta la dettatura vocale');
            return;
        }

        if (isRecording) {
            recognitionRef.current?.stop();
            setIsRecording(false);
        } else {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.lang = 'it-IT';
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = false;

            recognitionRef.current.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map(result => result[0].transcript)
                    .join(' ');
                setAddRawText(prev => prev ? `${prev} ${transcript}` : transcript);
            };

            recognitionRef.current.onerror = () => {
                setIsRecording(false);
            };

            recognitionRef.current.onend = () => {
                setIsRecording(false);
            };

            recognitionRef.current.start();
            setIsRecording(true);
        }
    };

    // Funzioni per modifica inline nella vista principale
    const startInlineEdit = (index) => {
        const mat = materials[index];
        setInlineEdit({
            quantity: mat.quantity || 1,
            unit: mat.unit || 'pz',
            description: mat.description || ''
        });
        setEditingIndex(index);
    };

    const cancelInlineEdit = () => {
        setEditingIndex(null);
        setInlineEdit({ quantity: 0, unit: '', description: '' });
    };

    const saveInlineEdit = async () => {
        if (editingIndex === null) return;
        
        const updatedMaterials = [...materials];
        updatedMaterials[editingIndex] = {
            ...updatedMaterials[editingIndex],
            quantity: inlineEdit.quantity,
            unit: inlineEdit.unit,
            description: inlineEdit.description
        };
        
        // Salva sul server
        try {
            const token = localStorage.getItem('installamelo-auth-token');
            const authUser = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
            const userId = authUser.userId;
            const userLabel = localStorage.getItem('userLabel') || 'Utente';

            const response = await axios.patch(
                getNodeApiUrl('/jobs/materials/update'),
                {
                    jobId,
                    materials: updatedMaterials,
                    materialsRawText,
                    userId,
                    userLabel
                },
                { headers: { 'auth-token': token } }
            );

            if (response.data?.success) {
                setMaterials(response.data.materials || []);
                cancelInlineEdit();
            }
        } catch (err) {
            console.error('Errore salvataggio modifica inline:', err);
        }
    };

    // Funzioni per eliminazione multipla
    const toggleMarkForDeletion = (index) => {
        setMarkedForDeletion(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const cancelDeletions = () => {
        setMarkedForDeletion(new Set());
    };

    const saveDeletions = async () => {
        if (markedForDeletion.size === 0) return;
        
        setIsSavingDeletions(true);
        try {
            const token = localStorage.getItem('installamelo-auth-token');
            const authUser = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
            const userId = authUser.userId;
            const userLabel = localStorage.getItem('userLabel') || 'Utente';

            const updatedMaterials = materials.filter((_, idx) => !markedForDeletion.has(idx));

            const response = await axios.patch(
                getNodeApiUrl('/jobs/materials/update'),
                {
                    jobId,
                    materials: updatedMaterials,
                    materialsRawText,
                    userId,
                    userLabel
                },
                { headers: { 'auth-token': token } }
            );

            if (response.data?.success) {
                setMaterials(response.data.materials || []);
                setMarkedForDeletion(new Set());
            }
        } catch (err) {
            console.error('Errore eliminazione materiali:', err);
        } finally {
            setIsSavingDeletions(false);
        }
    };

    const handlePrintPDF = async () => {
        if (!materials || materials.length === 0) {
            return;
        }

        const doc = new jsPDF();
        
        // Carica il logo
        try {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = LOGO_URL;
            });
            doc.addImage(img, 'PNG', 14, 10, 50, 18);
        } catch (e) {
            console.warn('Impossibile caricare il logo:', e);
        }
        
        // Intestazione
        doc.setFontSize(18);
        doc.setTextColor(0, 150, 136);
        doc.text('Lista Materiali', 14, 40);
        
        // Info commessa
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Commessa: ${jobCode || jobId}`, 14, 50);
        if (customerLabel) {
            doc.text(`Cliente: ${customerLabel}`, 14, 56);
        }
        
        // Tabella materiali
        const tableData = materials.map((mat, idx) => [
            idx + 1,
            mat.quantity || 1,
            mat.unit || 'pz',
            mat.description || '',
            mat.addedByLabel || '-'
        ]);
        
        autoTable(doc, {
            startY: 65,
            head: [['#', 'Qtà', 'Unità', 'Descrizione', 'Aggiunto da']],
            body: tableData,
            theme: 'striped',
            headStyles: { 
                fillColor: [0, 150, 136],
                textColor: 255,
                fontStyle: 'bold'
            },
            columnStyles: {
                0: { cellWidth: 12, halign: 'center' },
                1: { cellWidth: 15, halign: 'center' },
                2: { cellWidth: 20, halign: 'center' },
                3: { cellWidth: 'auto' },
                4: { cellWidth: 35 }
            },
            styles: {
                fontSize: 9,
                cellPadding: 3
            }
        });
        
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150);
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.text(
                `Pagina ${i} di ${pageCount} - Generato il ${new Date().toLocaleDateString('it-IT')}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
        }
        
        // Salva il PDF
        const fileName = `materiali_${jobCode || jobId}.pdf`.replace(/\s+/g, '_');
        doc.save(fileName);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('it-IT');
    };

    if (isLoading) {
        return (
            <Card className="mb-3">
                <CardBody className="text-center py-4">
                    <Spinner size="sm" /> Caricamento materiali...
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            <Card className="mb-3">
                <CardHeader className="d-flex justify-content-between align-items-center py-2" style={{ backgroundColor: '#e0f7fa' }}>
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faBoxes} className="text-info me-2" />
                        <span className="fw-semibold">
                            Materiali Commessa {materials.length > 0 && `(${materials.length})`}
                        </span>
                    </div>
                    <div>
                        <Button color="success" size="sm" outline className="me-1" onClick={toggleAddPanel}>
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Aggiungi
                        </Button>
                        {materials.length > 0 && (
                            <Button color="danger" size="sm" outline onClick={handlePrintPDF}>
                                <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                PDF
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardBody className="py-2">
                    {error && <Alert color="danger" className="mb-2">{error}</Alert>}
                    
                    {/* Pannello di aggiunta materiali */}
                    {showAddPanel && (
                        <div className="mb-3 p-3 border rounded bg-light">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label fw-semibold mb-0">
                                    <FontAwesomeIcon icon={faPlus} className="me-2 text-success" />
                                    Aggiungi materiali
                                </label>
                                <Button color="link" size="sm" className="text-muted p-0" onClick={toggleAddPanel}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            </div>
                            <p className="text-muted small mb-2">
                                Scrivi o detta la lista dei materiali separati da virgola (es: 10 metri cavo 16mm, 5 quadri elettrici)
                            </p>
                            <InputGroup className="mb-2">
                                <Input
                                    type="textarea"
                                    rows={3}
                                    placeholder="Es: 10 metri cavo 16mm, 60 spine 16 Ampere, 15 quadri elettrici..."
                                    value={addRawText}
                                    onChange={(e) => setAddRawText(e.target.value)}
                                />
                                <Button 
                                    color={isRecording ? "danger" : "secondary"}
                                    onClick={toggleRecording}
                                    title={isRecording ? "Ferma registrazione" : "Inizia dettatura"}
                                >
                                    <FontAwesomeIcon icon={isRecording ? faMicrophoneSlash : faMicrophone} />
                                </Button>
                            </InputGroup>
                            {isRecording && (
                                <small className="text-danger d-block mb-2">
                                    🎤 Registrazione in corso... Parla e clicca per fermare.
                                </small>
                            )}
                            <Button 
                                color="success" 
                                onClick={handleParseAndAdd}
                                disabled={isParsing || !addRawText.trim()}
                            >
                                {isParsing ? (
                                    <><Spinner size="sm" className="me-2" />Elaborazione...</>
                                ) : (
                                    <><FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />Aggiungi alla lista</>
                                )}
                            </Button>
                        </div>
                    )}
                    
                    {materials.length === 0 ? (
                        <div className="text-muted text-center py-3">
                            <FontAwesomeIcon icon={faBoxes} size="2x" className="mb-2 text-secondary" />
                            <p className="mb-0">Nessun materiale inserito</p>
                            <small>Clicca "Aggiungi" per inserire materiali</small>
                        </div>
                    ) : (
                        <div>
                            <table className="table table-sm table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '70px' }}>Qtà</th>
                                        <th style={{ width: '80px' }}>Unità</th>
                                        <th>Descrizione</th>
                                        <th style={{ width: '120px' }}>Aggiunto da</th>
                                        <th style={{ width: '100px' }}>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {materials.map((mat, idx) => (
                                        <tr key={idx} className={markedForDeletion.has(idx) ? 'table-danger' : ''} style={markedForDeletion.has(idx) ? { opacity: 0.6, textDecoration: 'line-through' } : {}}>
                                            {editingIndex === idx ? (
                                                <>
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            bsSize="sm"
                                                            value={inlineEdit.quantity}
                                                            onChange={(e) => setInlineEdit({...inlineEdit, quantity: parseInt(e.target.value) || 1})}
                                                            min="1"
                                                            style={{ width: '60px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            value={inlineEdit.unit}
                                                            onChange={(e) => setInlineEdit({...inlineEdit, unit: e.target.value})}
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            value={inlineEdit.description}
                                                            onChange={(e) => setInlineEdit({...inlineEdit, description: e.target.value})}
                                                        />
                                                    </td>
                                                    <td></td>
                                                    <td className="text-center">
                                                        <Button color="success" size="sm" className="me-1 p-1" onClick={saveInlineEdit} title="Salva">
                                                            <FontAwesomeIcon icon={faSave} />
                                                        </Button>
                                                        <Button color="secondary" size="sm" className="p-1" onClick={cancelInlineEdit} title="Annulla">
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </Button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="fw-bold text-info">{mat.quantity}</td>
                                                    <td className="text-muted">{mat.unit}</td>
                                                    <td>{mat.description}</td>
                                                    <td className="small">
                                                        <span className="text-primary">
                                                            <FontAwesomeIcon icon={faUser} className="me-1" />
                                                            {mat.addedByLabel || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {markedForDeletion.has(idx) ? (
                                                            <Button 
                                                                color="secondary" 
                                                                size="sm" 
                                                                outline 
                                                                className="p-1"
                                                                onClick={() => toggleMarkForDeletion(idx)}
                                                                title="Annulla eliminazione"
                                                            >
                                                                <FontAwesomeIcon icon={faUndo} />
                                                            </Button>
                                                        ) : (
                                                            <>
                                                                <Button 
                                                                    color="info" 
                                                                    size="sm" 
                                                                    outline 
                                                                    className="p-1 me-1"
                                                                    onClick={() => startInlineEdit(idx)}
                                                                    title="Modifica"
                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </Button>
                                                                <Button 
                                                                    color="danger" 
                                                                    size="sm" 
                                                                    outline 
                                                                    className="p-1"
                                                                    onClick={() => toggleMarkForDeletion(idx)}
                                                                    title="Elimina"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </Button>
                                                            </>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {/* Barra azioni eliminazione multipla */}
                            {markedForDeletion.size > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 p-3 bg-danger bg-opacity-10 rounded border border-danger">
                                    <span className="text-danger fw-semibold">
                                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                                        {markedForDeletion.size} {markedForDeletion.size === 1 ? 'elemento selezionato' : 'elementi selezionati'} per eliminazione
                                    </span>
                                    <div>
                                        <Button 
                                            color="secondary" 
                                            size="sm" 
                                            outline 
                                            className="me-2"
                                            onClick={cancelDeletions}
                                            disabled={isSavingDeletions}
                                        >
                                            <FontAwesomeIcon icon={faUndo} className="me-1" />
                                            Annulla
                                        </Button>
                                        <Button 
                                            color="danger" 
                                            size="sm"
                                            onClick={saveDeletions}
                                            disabled={isSavingDeletions}
                                        >
                                            {isSavingDeletions ? (
                                                <><Spinner size="sm" className="me-1" />Eliminazione...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faSave} className="me-1" />Conferma Eliminazione</>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

        </>
    );
};

export default JobMaterialsPanel;
